/** Conformity on the campaign overview.
 *
 *  Counted per INSPECTION, while the funnel beside it counts
 *  BENEFICIARIES. Reported 2026-09-27: the two cards sat side by side
 *  showing "Pending 950" and "Pending 514" — same word, different noun,
 *  different denominator, and nothing on screen saying so. On the
 *  campaign that surfaced it the real figures were 1,007 beneficiaries
 *  against 527 inspections.
 *
 *  Two things fix that, and both are wording rather than maths: the
 *  heading states the denominator, and this card says "not yet
 *  answered" where the funnel says "pending".
 */
import { useTranslation } from "react-i18next";

export function ConformityCard({
  conform,
  nonConform,
  pending,
}: {
  conform: number;
  nonConform: number;
  pending: number;
}) {
  const { t } = useTranslation();
  const total = conform + nonConform + pending;
  const judged = conform + nonConform;
  const failPct = judged > 0 ? Math.round((nonConform / judged) * 100) : 0;
  const rows = [
    { key: "conform", value: conform, bar: "bg-emerald-500" },
    { key: "nonConform", value: nonConform, bar: "bg-red-600" },
    { key: "unanswered", value: pending, bar: "bg-slate-500" },
  ] as const;

  return (
    <section className="card space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
        {t("campaigns.detail.conformityTitle", "Conformity")}
        <span className="ml-2 font-mono normal-case tracking-normal text-nb-text-3">
          {t("campaigns.detail.conformityOf", { count: total })}
        </span>
      </h2>

      {/* One stacked bar, as the funnel above it uses — the three
          values are parts of one whole, so they should read as one bar
          rather than three unrelated rows. */}
      <div className="flex h-2.5 overflow-hidden rounded-full bg-nb-bg/60">
        {rows.map((r) => (
          <span
            key={r.key}
            className={r.bar}
            style={{ width: `${total > 0 ? (r.value / total) * 100 : 0}%` }}
          />
        ))}
      </div>

      {/* See CampaignFunnelBars: two columns clipped "Not yet answered"
          in the rail. */}
      <ul className="flex flex-col gap-1 text-sm">
        {rows.map((r) => (
          <li key={r.key} className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className={`h-2 w-2 shrink-0 rounded-sm ${r.bar}`}
            />
            <span className="truncate text-nb-text-2">
              {t(`campaigns.detail.conformity_${r.key}`)}
            </span>
            <span className="ml-auto font-mono tabular-nums">{r.value}</span>
          </li>
        ))}
      </ul>

      <p className="border-t border-nb-border pt-2.5 text-xs text-nb-text-2">
        {t("campaigns.detail.conformityJudged", { judged, total })}
      </p>
      {/* A failure rate off a handful of judgements is noise; past that
          it is the most important number on the card. */}
      {judged >= 10 && failPct >= 50 && (
        <p className="text-xs text-amber-300">
          {t("campaigns.detail.conformityFailRate", { pct: failPct })}
        </p>
      )}
    </section>
  );
}
